/**
 * Analytics utility for tracking user events.
 * Currently logs to console. Can be replaced with Mixpanel, GA4, or other analytics service.
 * 
 * Usage:
 * trackEvent('section_scroll_depth', { section_id: 'hero', depth: 50 })
 * trackEvent('cta_click', { section_id: 'hero', cta_type: 'try_free' })
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Track an analytics event
 * @param {string} eventName - Name of the event (e.g., 'section_scroll_depth', 'cta_click')
 * @param {object} properties - Event properties/metadata
 */
export function trackEvent(eventName, properties = {}) {
  const timestamp = new Date().toISOString();
  const event = {
    event: eventName,
    timestamp,
    ...properties
  };

  // Log to console in development
  if (isDevelopment) {
    console.log('[Analytics]', eventName, properties);
  }

  // TODO: Replace with actual analytics service (Mixpanel, GA4, etc.)
  // Example for Mixpanel:
  // if (window.mixpanel) {
  //   window.mixpanel.track(eventName, properties);
  // }
  
  // Example for GA4:
  // if (window.gtag) {
  //   window.gtag('event', eventName, properties);
  // }

  // Store in sessionStorage for debugging/testing
  try {
    const events = JSON.parse(sessionStorage.getItem('analytics_events') || '[]');
    events.push(event);
    // Keep only last 100 events to avoid storage limits
    if (events.length > 100) events.shift();
    sessionStorage.setItem('analytics_events', JSON.stringify(events));
  } catch (e) {
    // Silently fail if sessionStorage is unavailable
  }
}

/**
 * Track page view
 * @param {string} pageName - Name of the page
 * @param {object} properties - Additional properties
 */
export function trackPageView(pageName, properties = {}) {
  trackEvent('page_view', { page_name: pageName, ...properties });
}

/**
 * Get all tracked events (for debugging)
 * @returns {Array} Array of tracked events
 */
export function getTrackedEvents() {
  try {
    return JSON.parse(sessionStorage.getItem('analytics_events') || '[]');
  } catch (e) {
    return [];
  }
}

/**
 * Clear all tracked events
 */
export function clearTrackedEvents() {
  try {
    sessionStorage.removeItem('analytics_events');
  } catch (e) {
    // Silently fail
  }
}
